import {
  PROVINCE_ONTARIO,
  PROVINCE_ALBERTA,
  PROVINCE_NEW_BRUNSWICK,
  PROVINCE_NOVA_SCOTIA,
  LOB_PERSONAL_AUTO,
  LOB_PERSONAL_PROPERTY,
} from 'variables/constants';
import type { DashReportDriver, PropertyPolicy } from 'api';

export type { PropertyPolicy };

export interface ConsentRecord {
  license_number: string;
  consent_id: number;
}

interface BaseInsuranceHistoryData {
  continuously_insured_since?: string;
  year_policy_first_held_with_broker?: string;
  multi_policy_discount?: boolean;
  apply_for_all_carriers?: boolean;
  cancelled_for_non_payment?: boolean | null;
  num_cancellations?: number | null;
  date_of_last_cancellation?: string | null;
  date_of_cancellation_end?: string | null;
  cancelled_for_fraud?: boolean | null;
  date_of_fraud_cancellation?: string | null;
  cancelled_for_misrepresentation?: boolean | null;
  date_of_misrepresentation_cancellation?: string | null;
  date_of_misrepresentation_cancellation_end?: string | null;
  date_of_fraud_cancellation_end?: string | null;
}

export interface AutoInsuranceHistoryData extends BaseInsuranceHistoryData {
  auto_current_insured_since?: string;
  current_auto_insurer?: string;
}

export interface PropertyInsuranceHistoryData extends BaseInsuranceHistoryData {
  property_current_insured_since?: string;
  current_habitational_insurer?: string;
  prior_policy_number?: string;
  has_prior_insurance?: boolean;
  multi_property_discount?: boolean;
  auto_current_insured_since?: string;
  current_auto_insurer?: string;
  membership_discount_membership_group?: string;
  membership_discount_membership_number?: string;
  membership_discount_membership_effective_date?: string;
  additional_membership_discount_membership_group?: string;
  additional_membership_discount_membership_number?: string;
  additional_membership_discount_membership_effective_date?: string;
}

export type InsuranceHistoryData =
  | AutoInsuranceHistoryData
  | PropertyInsuranceHistoryData;

interface BasePolicyInfoData {
  id: number;
  province: number;
  effective_date: string;
  consents?: ConsentRecord[];
}

export interface AutoPolicyInfoData extends BasePolicyInfoData {
  line_of_business: typeof LOB_PERSONAL_AUTO;
  policy_info: {
    vehicle_type?: string;
    business_name_vehicles?: boolean;
    business_name?: string;
    policy_discount?: string;
    full_history_dash_dash_report_drivers?: DashReportDriver[];
    insurance_history?: AutoInsuranceHistoryData;
    property_policy?: PropertyPolicy;
  };
}

export interface PropertyPolicyInfoData extends BasePolicyInfoData {
  line_of_business: typeof LOB_PERSONAL_PROPERTY;
  policy_info: {
    property_policy?: PropertyPolicy;
    customer_type?: string;
    insurance_history?: PropertyInsuranceHistoryData;
  };
}

export type PolicyInfoData = AutoPolicyInfoData | PropertyPolicyInfoData;

export interface PolicyComponentProps {
  onFieldChange: (field: string, value: any) => void;
  isUpdating?: boolean;
}

export interface AutoPolicyBasicInfoProps extends PolicyComponentProps {
  province: number;
  vehicleType: string;
  effectiveDate: string;
  businessNameVehicles: boolean;
  businessName: string;
}

export interface PolicyDiscountProps extends PolicyComponentProps {
  policyDiscount: string;
}

export interface DashReportSectionProps {
  quoteId: number;
  province: number;
  drivers: DashReportDriver[];
  onDriversChange: (drivers: DashReportDriver[]) => void;
  onVehiclesPopulated?: () => void;
}

export const VEHICLE_TYPES = [
  { value: 'private_passenger', label: 'Private Passenger' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'miscellaneous', label: 'Miscellaneous' },
];

export const POLICY_DISCOUNTS = [
  { value: 'combined', label: 'Combined Policy / Multi-Line' },
  { value: 'multi_line', label: 'Multi-Line' },
  { value: 'disabled', label: 'Disabled' },
];

export const QUOTE_TYPES = [
  { value: 'new_business', label: 'New Business' },
  { value: 'remarket', label: 'Remarket' },
  { value: 'loa', label: 'LOA' },
];

export const CUSTOMER_TYPES = [
  { value: 'individual', label: 'Individual' },
  { value: 'corporation', label: 'Corporation' },
];

export interface PropertyPolicyBasicInfoProps extends PolicyComponentProps {
  effectiveDate: string;
  customerType: string;
}

export interface InsuranceHistoryCardProps extends PolicyComponentProps {
  insuranceHistory: PropertyInsuranceHistoryData;
  onInsuranceHistoryChange: (
    data: Partial<PropertyInsuranceHistoryData>,
  ) => void;
}

export { SALUTATIONS } from '../../constants/personOptions';

export const SUPPORTED_PROVINCES = [
  { value: 'ON', label: 'Ontario' },
  { value: 'AB', label: 'Alberta' },
  { value: 'NB', label: 'New Brunswick' },
  { value: 'NS', label: 'Nova Scotia' },
];

export const SUPPORTED_PROVINCES_NUMERIC = [
  { value: PROVINCE_ONTARIO, label: 'Ontario' },
  { value: PROVINCE_ALBERTA, label: 'Alberta' },
  { value: PROVINCE_NEW_BRUNSWICK, label: 'New Brunswick' },
  { value: PROVINCE_NOVA_SCOTIA, label: 'Nova Scotia' },
];
